// ─── utils/links.js ───────────────────────────────────────────────────────────
// Turns <a href> elements from imported HTML into CleverStory link actions —
// the same { uid, type: 'link', value, newTab } shape the Actions panel saves.

import shortid from 'shortid';

const escapeAttr = (value) => String(value)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

/**
 * Returns the href when it points somewhere a link action can open.
 * Placeholder ("#"), in-page fragment ("#pricing") and javascript: hrefs have
 * nothing to resolve to in the imported page, so they are dropped.
 */
export function getUsableHref(anchorEl) {
    const href = (anchorEl?.getAttribute('href') || '').trim();
    if (!href || href.startsWith('#')) return null;
    if (/^javascript:/i.test(href)) return null;
    return href;
}

/** Builds a link action for an anchor, or null when its href is not usable. */
export function createLinkAction(anchorEl) {
    const href = getUsableHref(anchorEl);
    if (!href) return null;

    return {
        uid:    `action_${shortid.generate()}`,
        type:   'link',
        value:  href,
        newTab: anchorEl.getAttribute('target') === '_blank',
    };
}

/**
 * Wraps text-widget HTML in an <a data-action> pointing at `action`, the markup
 * the text widget's link tool writes (see widgets/text — mceInsertLink).
 */
export function wrapWithLink(html, action) {
    return `<a href="${escapeAttr(action.value)}" data-action="${action.uid}">${html}</a>`;
}

/**
 * Text-widget variant of getTextContentWithLineBreaks: same text + <br> output,
 * but every <a href> inside `el` is kept as a link and its action is pushed
 * onto `actions`.
 */
export function getTextContentWithLinks(el, actions) {
    const walk = (node) => {
        if (node.nodeType === Node.TEXT_NODE) return node.textContent;
        if (node.nodeType !== Node.ELEMENT_NODE) return '';

        const tag = node.tagName.toLowerCase();
        if (tag === 'br') return '<br>';

        const inner = Array.from(node.childNodes).map(walk).join('');
        if (tag !== 'a' || !inner.trim()) return inner;

        const action = createLinkAction(node);
        if (!action) return inner;

        actions.push(action);
        return wrapWithLink(inner, action);
    };

    return Array.from(el.childNodes).map(walk).join('').trim();
}

/**
 * When the text element itself is (or sits inside) an <a href> — e.g.
 * <a data-widget="text" href="…">Learn more</a> — its whole content is the link.
 */
export function linkWholeContent(el, html, actions) {
    if (!html) return html;

    const anchor = el.closest('a[href]');
    const action = anchor && createLinkAction(anchor);
    if (!action) return html;

    actions.push(action);
    return wrapWithLink(html, action);
}
